/**
 * useAlertFeed - Real-time alert notifications via WebSocket
 */
import { useState, useEffect, useCallback, useRef } from 'react'
import { getWebSocketManager, AlertFired } from '../lib/websocket'
import { useAuthStore } from '../stores/authStore'
import { useAlertStore } from '../stores/alertStore'
import type { AlertEvent, AlertSeverity } from '../types'

interface UseAlertFeedResult {
  isConnected: boolean
  lastAlert: AlertEvent | null
}

export function useAlertFeed(): UseAlertFeedResult {
  const [isConnected, setIsConnected] = useState(false)
  const [lastAlert, setLastAlert] = useState<AlertEvent | null>(null)
  const { user, token, isAuthenticated } = useAuthStore()
  const { setUnreadCount: _setUnreadCount } = useAlertStore()
  const toastCallbackRef = useRef<((alert: AlertFired) => void) | null>(null)

  // Allow external callback for toast notifications

  // Handle incoming alert
  const handleAlert = useCallback((data: AlertFired) => {
    // Convert to AlertEvent format for the store
    const event: AlertEvent = {
      id: data.event_id,
      alert_id: data.alert_id,
      ticker: data.ticker || '',
      message: data.body,
      severity: data.severity as AlertSeverity,
      triggered_at: data.fired_at,
      is_read: false
    }

    setLastAlert(event)

    // Update unread count in store
    const alertStore = useAlertStore.getState()
    alertStore.setUnreadCount(alertStore.unreadCount + 1)

    // Dispatch event for notification system (toast + browser notifications)
    dispatchAlertEvent(data)

    // Trigger toast notification callback if set
    if (toastCallbackRef.current) {
      toastCallbackRef.current(data)
    }
  }, [])

  // Connect when authenticated
  useEffect(() => {
    if (!isAuthenticated || !user || !token) {
      return
    }

    const wsManager = getWebSocketManager()

    // Track connection status
    const unsubscribeStatus = wsManager.onStatusChange((channel, status) => {
      if (channel === 'alerts') {
        setIsConnected(status === 'connected')
      }
    })

    // Connect to alerts channel
    wsManager.connectAlerts(user.id, token, handleAlert)

    return () => {
      unsubscribeStatus()
      wsManager.disconnectChannel('alerts')
    }
  }, [isAuthenticated, user?.id, token, handleAlert])

  return { isConnected, lastAlert }
}

// Hook for registering toast callback
export function useAlertToastCallback(callback: (alert: AlertFired) => void) {
  const callbackRef = useRef(callback)
  callbackRef.current = callback

  useEffect(() => {
    // Store callback globally so useAlertFeed can access it
    const handler = (event: CustomEvent<AlertFired>) => {
      callbackRef.current(event.detail)
    }

    window.addEventListener('apex:alert' as any, handler)
    return () => window.removeEventListener('apex:alert' as any, handler)
  }, [])
}

// Utility to dispatch alert events for toast system
export function dispatchAlertEvent(alert: AlertFired) {
  window.dispatchEvent(new CustomEvent('apex:alert', { detail: alert }))
}

export default useAlertFeed
